import React, { useEffect, useState, memo } from 'react';
import { GlassesIcon, SmartphoneIcon, MonitorIcon, CloudIcon, BuildingIcon, ChevronRightIcon, PlayCircleIcon, CheckCircleIcon, ArrowRightIcon, ZapIcon, ShieldIcon, UsersIcon, TrendingUpIcon, MessageSquareIcon, SettingsIcon, FileIcon, VideoIcon, MicIcon, BrainIcon, LockIcon, BarChart3Icon, CodeIcon, PackageIcon, GitBranchIcon, TerminalIcon, BookOpenIcon, MenuIcon, XIcon, PlugIcon, SmartphoneIcon as AndroidIcon, LayersIcon, SparklesIcon } from 'lucide-react';
export function AriOSMarketing() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('home');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    deviceType: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  // Set page title
  useEffect(() => {
    document.title = 'AR Operating System';
  }, []);
  // Track mouse position for parallax effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  // Helper function to get border color based on section
  const getSectionColor = (section: string) => {
    const colorMap: {
      [key: string]: string;
    } = {
      home: '#E9A165',
      'ari-os': '#E9A165',
      chat: '#D25796',
      cloud: '#8846DE',
      business: '#D25796',
      'mobile-app': '#8846DE',
      architecture: '#E9A165',
      developers: '#8846DE'
    };
    return colorMap[section] || '#E9A165';
  };
  const getActiveButtonClass = (section: string) => {
    return activeSection === section ? `border-2 text-white` : 'text-gray-300 hover:text-white hover:bg-white/5 border-2 border-transparent';
  };
  const getActiveBorderStyle = (section: string) => {
    if (activeSection === section) {
      return {
        borderColor: getSectionColor(section)
      };
    }
    return {};
  };
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      // Determine which section is currently in view
      const sections = ['home', 'ari-os', 'chat', 'cloud', 'business', 'mobile-app', 'architecture', 'developers'];
      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Check if section is in the middle of viewport
          if (rect.top <= 300 && rect.bottom >= 300) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 40;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    setTimeout(() => {
      setContactModalOpen(false);
      setFormSubmitted(false);
      setFormData({
        name: '',
        email: '',
        company: '',
        deviceType: '',
        message: ''
      });
    }, 2000);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <div className="w-full min-h-screen bg-black relative overflow-hidden" style={{
    zoom: '0.9'
  }} data-id="element-35" data-mp="209qg18ojfov5">
      {/* Enhanced Animated Background with Apple-style depth */}
      <div className="fixed inset-0 z-0" data-id="element-36" data-mp="1rd92xzwm5gwr">
        {/* Base gradient with smooth transitions */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-zinc-900 transition-all duration-1000" data-id="element-37" data-mp="1mdmqhsom04p0"></div>

        {/* Animated gradient orbs with parallax */}
        <div className="absolute inset-0 opacity-30 transition-all duration-700 ease-out" style={{
        transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
      }} data-id="element-38" data-mp="3gp1f265vvy36">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-radial from-[#E9A165]/20 via-[#E9A165]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" data-id="element-39" data-mp="3o92dsshe8ya2"></div>
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-radial from-[#8846DE]/20 via-[#8846DE]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '1s'
        }} data-id="element-40" data-mp="1trcsagsuvpg"></div>
          <div className="absolute bottom-1/4 left-1/3 w-[550px] h-[550px] bg-gradient-radial from-[#D25796]/20 via-[#D25796]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-41" data-mp="35ckf1r44ny4b"></div>
        </div>

        {/* Glossy mesh gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(233,161,101,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(136,70,222,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(210,87,150,0.12),transparent_50%)] opacity-60" data-id="element-42" data-mp="uhn6d6b2o7i0"></div>

        {/* Subtle animated grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] opacity-20 animate-grid-flow" data-id="element-43" data-mp="2zlpt8fuk1mz0"></div>

        {/* Glass reflection effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent" data-id="element-44" data-mp="22wrygxmt1p1u"></div>
      </div>

      {/* Sticky Header Navigation */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-zinc-950/80 backdrop-blur-xl border-b border-zinc-800 shadow-2xl' : 'bg-transparent'}`} data-id="element-45" data-mp="2bwoqn98el61n">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-46" data-mp="1k5qz48hrvrhr">
          <div className="flex items-center justify-between" data-id="element-47" data-mp="1k4zchn65l43m">
            {/* Logo */}
            <button onClick={() => scrollToSection('home')} className="flex items-center space-x-3 group" data-id="element-48" data-mp="1a0shy42rx5z6">
              <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-8 group-hover:scale-110 transition-transform" data-id="element-49" data-mp="103eer65v2dot" />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-2" data-id="element-50" data-mp="1xr4zm6ntmh2y">
              <button onClick={() => scrollToSection('home')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('home')}`} style={getActiveBorderStyle('home')} data-id="element-51" data-mp="1xqd9vcgwndkl">
                Home
              </button>
              <button onClick={() => scrollToSection('ari-os')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('ari-os')}`} style={getActiveBorderStyle('ari-os')} data-id="element-52" data-mp="7gtipsefxdex">
                Ari OS
              </button>
              <button onClick={() => scrollToSection('chat')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('chat')}`} style={getActiveBorderStyle('chat')} data-id="element-53" data-mp="3fuhh9qc8plre">
                Web
              </button>
              <button onClick={() => scrollToSection('cloud')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('cloud')}`} style={getActiveBorderStyle('cloud')} data-id="element-54" data-mp="7exsb3ilqpi5">
                Cloud
              </button>
              <button onClick={() => scrollToSection('business')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('business')}`} style={getActiveBorderStyle('business')} data-id="element-55" data-mp="v4ia3j30rgpu">
                Business
              </button>
              <button onClick={() => scrollToSection('mobile-app')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('mobile-app')}`} style={getActiveBorderStyle('mobile-app')} data-id="element-56" data-mp="2e06001xmooik">
                Mobile App
              </button>
              <button onClick={() => scrollToSection('architecture')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('architecture')}`} style={getActiveBorderStyle('architecture')} data-id="element-57" data-mp="2rxbduo3l2rod">
                Architecture
              </button>
              <button onClick={() => scrollToSection('developers')} className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('developers')}`} style={getActiveBorderStyle('developers')} data-id="element-58" data-mp="3aqeyxl2qjw7d">
                Developers
              </button>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-3" data-id="element-59" data-mp="2to7qcptl01pt">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-6 py-2 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all text-sm font-semibold" data-id="element-60" data-mp="2tlv07pdo6l1o">
                Try Ari
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-61" data-mp="3hdtmf4enq0tl">
              {mobileMenuOpen ? <XIcon size={24} data-id="element-62" data-mp="1n0akzjhi9y30" /> : <MenuIcon size={24} data-id="element-63" data-mp="1mzsoclqxlhuk" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && <div className="lg:hidden mt-4 pb-4 border-t border-white/10 pt-4 space-y-2 bg-zinc-950/95 backdrop-blur-xl rounded-b-2xl" data-id="element-64" data-mp="1apjjlroyf954">
              <button onClick={() => scrollToSection('home')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('home')}`} style={getActiveBorderStyle('home')} data-id="element-65" data-mp="15qzxnrad1xcr">
                Home
              </button>
              <button onClick={() => scrollToSection('ari-os')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('ari-os')}`} style={getActiveBorderStyle('ari-os')} data-id="element-66" data-mp="1tdojup6ekbz6">
                Ari OS
              </button>
              <button onClick={() => scrollToSection('chat')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('chat')}`} style={getActiveBorderStyle('chat')} data-id="element-67" data-mp="907e2jo7xn68">
                Chat
              </button>
              <button onClick={() => scrollToSection('cloud')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('cloud')}`} style={getActiveBorderStyle('cloud')} data-id="element-68" data-mp="1fdc36cykfly">
                Cloud
              </button>
              <button onClick={() => scrollToSection('business')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('business')}`} style={getActiveBorderStyle('business')} data-id="element-69" data-mp="3so0x1l7ae0sz">
                Business
              </button>
              <button onClick={() => scrollToSection('mobile-app')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('mobile-app')}`} style={getActiveBorderStyle('mobile-app')} data-id="element-70" data-mp="39vyo6w0vzota">
                Mobile App
              </button>
              <button onClick={() => scrollToSection('architecture')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('architecture')}`} style={getActiveBorderStyle('architecture')} data-id="element-71" data-mp="2zco71ri1q43i">
                Architecture
              </button>
              <button onClick={() => scrollToSection('developers')} className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm font-medium ${getActiveButtonClass('developers')}`} style={getActiveBorderStyle('developers')} data-id="element-72" data-mp="3izlv20m8f0bd">
                Developers
              </button>
              <div className="pt-4 space-y-2" data-id="element-73" data-mp="3n2iiq5qmpmm6">
                <a href="https://app.ari-os.com" target="_blank" rel="noopener noreferrer" className="w-full px-4 py-3 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-lg hover:shadow-xl transition-all text-sm font-semibold block text-center" data-id="element-74" data-mp="1lc1lpkybhmxg">
                  Try Ari
                </a>
              </div>
            </div>}
        </div>
      </header>

      {/* Main Content - All sections in one flow */}
      <div className="relative z-10" data-id="element-75" data-mp="17caafrz6qkn5">
        {/* Hero Section */}
        <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-6" data-id="element-76" data-mp="1bd2kuojsmvl0">
          <div className="max-w-7xl mx-auto text-center" data-id="element-77" data-mp="1ge028ywzbllg">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-12 mb-8 leading-tight" data-id="element-78" data-mp="2mt0oue046rfd">
              Voice-First OS for the
              <br data-id="element-79" data-mp="2rq0y0xs9gaap" />
              <span className="bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent animate-gradient" data-id="element-80" data-mp="2rtomhiium6ik">
                Next Generation of Devices
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-4xl mx-auto leading-relaxed" data-id="element-81" data-mp="2s5h91r4k41se">
              Work hands-free across all your devices with AI-powered voice
              control and intelligent cloud sync
            </p>

            {/* CTA Buttons - Updated to scroll instead of navigate */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-82" data-mp="2e5euadrjpohd">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg group relative overflow-hidden" data-id="element-83" data-mp="296bpmufh9k9x">
                <span className="relative z-10" data-id="element-84" data-mp="1wwm2ffz8nxxz">Try Ari Now</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-85" data-mp="1wvazfidmau7x"></div>
              </a>
              <button onClick={() => scrollToSection('architecture')} className="px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-86" data-mp="1nxihe6gih8gu">
                Ecosystem Architecture
              </button>
            </div>

            {/* Early Access CTA */}
            <div className="flex justify-center mb-16" data-id="element-87" data-mp="26lx5akvj86df">
              <a href="https://forms.cloud.microsoft/pages/responsepage.aspx?id=xMS4P2WNLUSAk22HkPxrhRXI1sYi83xFmdd8cOAxygVUM040Wk1XUFo2R0M5S0ZZMkFNVllCSDVYVi4u" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.04] backdrop-blur-xl border border-[#E9A165]/30 hover:border-[#E9A165]/60 hover:bg-white/[0.08] transition-all" data-id="element-88" data-mp="2bl0bw9r5ukni">
                <span className="px-2.5 py-1 rounded-full bg-[#E9A165]/15 border border-[#E9A165]/30 text-[#E9A165] text-xs font-semibold uppercase tracking-wider" data-id="element-89" data-mp="11nidgxf13wkz">
                  Early Access
                </span>
                <span className="text-white font-semibold text-base" data-id="element-90" data-mp="8ybj8amlxt5j">
                  Sign up for Ari OS on the RealWear Navigator Z1
                </span>
                <ArrowRightIcon size={18} className="text-[#E9A165] group-hover:translate-x-1 transition-transform" data-id="element-91" data-mp="3xox4xd5d7uq" />
              </a>
            </div>

            {/* Video Placeholder */}
            <div className="relative max-w-6xl mx-auto" data-id="element-92" data-mp="3mczftjniuf30">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-93" data-mp="3mbof5spdhng4"></div>
            </div>
          </div>
        </section>

        {/* Ari OS Section */}
        <section id="ari-os" className="min-h-screen flex items-center py-32 px-6" data-id="element-94" data-mp="30u69xngd0c43">
          <div className="max-w-7xl mx-auto" data-id="element-95" data-mp="35t9hpfnq4wfr">
            <div className="text-center mb-16" data-id="element-96" data-mp="35vlxpn9x8wq6">
              <div className="inline-block mb-6" data-id="element-97" data-mp="3asvvprbdmgpm">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-98" data-mp="3aqj38k7zsnyb">
                  <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-4 inline-block" data-id="element-99" data-mp="3jsimzkae664y" />
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-100" data-mp="2csotns38w2c">
                Ari on Device
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-101" data-mp="6dmbyexpi6u6">
                Control your entire device with just your voice, hands-free
                productivity at its finest
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-102" data-mp="1mu1qou5z53dv">
              <div data-id="element-103" data-mp="1hwtct5ruzlif">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-104" data-mp="1hw0bikbo5o7o">
                  Voice-First Interface
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-105" data-mp="2akfol1b8pwgd">
                  Control everything with natural voice commands. Ari
                  understands context and responds intelligently to help you
                  stay productive.
                </p>
                <div className="space-y-6" data-id="element-106" data-mp="20sro6awph52r">
                  <div className="flex items-start space-x-4 group" data-id="element-107" data-mp="1vw1dy47l13p9">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-108" data-mp="1vtoufmm6ela3">
                      <MicIcon size={24} className="text-white" data-id="element-109" data-mp="367fcl6l9duyw" />
                    </div>
                    <div data-id="element-110" data-mp="3652o0akzwmcv">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-111" data-mp="317t1p3swkqt9">
                        Natural Language Processing
                      </h4>
                      <p className="text-zinc-500" data-id="element-112" data-mp="2w9j3slfsnc3y">
                        Speak naturally, Ari understands intent and context
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-113" data-mp="3ox5ai2kbiwv1">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-114" data-mp="3k050jgkcl9yn">
                      <BrainIcon size={24} className="text-white" data-id="element-115" data-mp="3b5qnzlr7cdj8" />
                    </div>
                    <div data-id="element-116" data-mp="bjtraf2kc2ef">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-117" data-mp="bkbn5albcelt">
                        Contextual Awareness
                      </h4>
                      <p className="text-zinc-500" data-id="element-118" data-mp="1pfla2pt6h2nd">
                        Remembers previous conversations and preferences
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-119" data-mp="1yf8d1g1k2gij">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-120" data-mp="1yi2xv8ohe3ch">
                      <ZapIcon size={24} className="text-white" data-id="element-121" data-mp="2dd0sk873r4wq" />
                    </div>
                    <div data-id="element-122" data-mp="2dao2f7r6xo8l">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-123" data-mp="2i37eu1e56dbt">
                        Instant Response
                      </h4>
                      <p className="text-zinc-500" data-id="element-124" data-mp="2m4szi8wx3vwk">
                        Lightning-fast processing for real-time assistance
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-125" data-mp="w18wvk495vfm">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-126" data-mp="w20rosq0zp4u"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-127" data-mp="r032lhuy203z">
                  <img src="https://cdn.magicpatterns.com/uploads/7sJmhbQwXp2rt8FjRa293u/devices_new_3.png" alt="Ari OS Devices Ecosystem" className="w-full h-full object-cover" data-id="element-128" data-mp="10yis5gebp1bd" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-129" data-mp="1jr36ztx6t5w6">
              <div className="order-2 lg:order-1 relative" data-id="element-130" data-mp="1jp0kvkz5nwwq">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-131" data-mp="3qi2qowumt235"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-132" data-mp="3lkjbnuq40la5">
                  <img src="https://cdn.magicpatterns.com/uploads/nT6uLZCb3k3Aij25WepRHP/person_talking_2.png" alt="Hands-Free Control Demo - Person using AR glasses with voice commands" className="w-full h-full object-cover" data-id="element-133" data-mp="1racf7jtyiyjn" />
                </div>
              </div>
              <div className="order-1 lg:order-2" data-id="element-134" data-mp="1n9it8w02rtte">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-135" data-mp="18bi2shhyua6n">
                  Complete Voice Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-136" data-mp="1d9rz4vfffgtu">
                  Take pictures, make calls, reply to emails, send messages, and
                  control your entire device - all hands-free with just your
                  voice.
                </p>
                <div className="space-y-6" data-id="element-137" data-mp="17m320yakm5jq">
                  <div className="flex items-start space-x-4 group" data-id="element-138" data-mp="17ltc1apizye1">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-139" data-mp="13khtfp52kdfc">
                      <GlassesIcon size={24} className="text-white" data-id="element-140" data-mp="tlij824no9q8" />
                    </div>
                    <div data-id="element-141" data-mp="onqq54s9yhkg">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-142" data-mp="op1ub5pmtrc3">
                        Hands-Free Photography
                      </h4>
                      <p className="text-zinc-500" data-id="element-143" data-mp="fux9p413dc50">
                        Take pictures and videos without touching your device
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-144" data-mp="5wpvgsy6t86p">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-145" data-mp="5z3s368imsgd">
                      <VideoIcon size={24} className="text-white" data-id="element-146" data-mp="3n1pzit7r6xhi" />
                    </div>
                    <div data-id="element-147" data-mp="3s0jd0ln1ioho">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-148" data-mp="sf2z3jy2rf6">
                        Microsoft Teams & Calls
                      </h4>
                      <p className="text-zinc-500" data-id="element-149" data-mp="5qqd7fe5j3xx">
                        Make calls on Teams and other platforms with voice
                        commands
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-150" data-mp="3e71xdbxh5vwx">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-151" data-mp="3e5qst9w7gk4r">
                      <MessageSquareIcon size={24} className="text-white" data-id="element-152" data-mp="2li4hffgqkb7c" />
                    </div>
                    <div data-id="element-153" data-mp="2qfcyfcq5e970">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-154" data-mp="2igrqq9hsnon0">
                        Email & Messaging
                      </h4>
                      <p className="text-zinc-500" data-id="element-155" data-mp="2dgx2kdpeld5n">
                        Reply to emails and send messages using only your voice
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-156" data-mp="2n8t4y0yivpfo">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-157" data-mp="1zh441w7mxsn4">
                      <MicIcon size={24} className="text-white" data-id="element-158" data-mp="29gd5190x47iz" />
                    </div>
                    <div data-id="element-159" data-mp="24ggzh5cko81b">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-160" data-mp="24g7clqmtqgzu">
                        Talk to Ari
                      </h4>
                      <p className="text-zinc-500" data-id="element-161" data-mp="3kx37qi64uh7u">
                        Ask questions and get instant answers from your AI
                        assistant
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Web Portal Section */}
        <section id="chat" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-zinc-950" data-id="element-162" data-mp="3egbdio6zoudq">
          <div className="max-w-7xl mx-auto" data-id="element-163" data-mp="a0s6hvznsrga">
            <div className="text-center mb-16" data-id="element-164" data-mp="a235zjm2nd1l">
              <div className="inline-block mb-6" data-id="element-165" data-mp="ezv7n5ciqdiz">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-166" data-mp="eyjx8moki7iw">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-167" data-mp="5a1vo38grwe7">
                    WEB
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-168" data-mp="xqme9z54fpto">
                Conversational Interface
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-169" data-mp="911bkqx5r7l5">
                Talk to Ari from anywhere - your computer, headset, or mobile
                device
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16" data-id="element-170" data-mp="3vfksovdip0vb">
              <div className="relative" data-id="element-171" data-mp="3qhrhtp19riop">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-172" data-mp="ryawkci4st4i"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-173" data-mp="n0j67mx4xf2f">
                  <img src="https://cdn.magicpatterns.com/uploads/6F5gyPGn14eCsFN6uaP8Ti/image.png" alt="Ari Chat Interface - Voice-Powered Assistant" className="w-full h-full object-cover" data-id="element-174" data-mp="d0ikxvfpgovl" />
                </div>
              </div>
              <div data-id="element-175" data-mp="1db309fkrwuax">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-176" data-mp="1d7nl6cr3nl9i">
                  Voice-Powered Assistant
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-177" data-mp="1n764qs33l511">
                  Use your voice to talk to Ari and get help with emails,
                  messages, calendar, and any questions you have. Chat from your
                  computer, headset, or mobile device.
                </p>
                <div className="space-y-4" data-id="element-178" data-mp="20yb7w45ocfg4">
                  <div className="flex items-start space-x-3" data-id="element-179" data-mp="20x9lx4n4e8jm">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-180" data-mp="25z7b0fi7bxkh">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-181" data-mp="2awh7ug7x7bic" />
                    </div>
                    <p className="text-zinc-400" data-id="element-182" data-mp="blzac77jorqx">
                      Help with mail, messages, and calendar
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-183" data-mp="24rdvvokon6e">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-184" data-mp="6vrbz49fhbah">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-185" data-mp="3od0rkup4j2gg" />
                    </div>
                    <p className="text-zinc-400" data-id="element-186" data-mp="3taj5kzi007vb">
                      Answer any question instantly
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-187" data-mp="ktfw29vo8eeh">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-188" data-mp="ptao4gr6na14">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-189" data-mp="azfkpq9sqcxb" />
                    </div>
                    <p className="text-zinc-400" data-id="element-190" data-mp="fyioz8hycf48">
                      Chat from computer, headset, or mobile
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-191" data-mp="30npxtx66y8bt">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-192" data-mp="1ip1tpnynixv8">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-193" data-mp="1dovunmq1y934" />
                    </div>
                    <p className="text-zinc-400" data-id="element-194" data-mp="1rooviavam5ot">
                      Supports all major languages
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-195" data-mp="zwg0lbf4plec">
              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-196" data-mp="v1afgetjwarb">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-197" data-mp="13ykqq06dbzw8">
                  <MessageSquareIcon size={24} className="text-white" data-id="element-198" data-mp="c8y3d0d3hv3z" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-199" data-mp="c754w8cmyh72">
                  Conversation History
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-200" data-mp="2gcas176cdnmk">
                  View and search through all your past conversations with Ari
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-201" data-mp="2l5vk6il97ndu">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-202" data-mp="1sgyf5h61zn31">
                  <GlassesIcon size={24} className="text-white" data-id="element-203" data-mp="22ci39hedlhh8" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-204" data-mp="27buueeca5apm">
                  Switch Between Devices
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-205" data-mp="3dr57rj8aarqa">
                  Seamlessly switch chats between headset and web interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-206" data-mp="173oc38lgcrkh">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-207" data-mp="xe4rnyi3cpni">
                  <ZapIcon size={24} className="text-white" data-id="element-208" data-mp="se079xe2bas0" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-209" data-mp="ng74zffvr0x0">
                  All Ari Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-210" data-mp="jdkat7vql3wn">
                  Access all of Ari's capabilities from the chat interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-211" data-mp="9gpacjfwktfq">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-212" data-mp="23ugjlhublban">
                  <UsersIcon size={24} className="text-white" data-id="element-213" data-mp="11i3rrqt6t5t4" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-214" data-mp="15hcmaxw1x7pq">
                  Manage Profile
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-215" data-mp="ri363y7uidlr">
                  Update your profile and subscription status anytime
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-216" data-mp="hs9yf8atqims">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-217" data-mp="8sda57scjls9">
                  <SettingsIcon size={24} className="text-white" data-id="element-218" data-mp="drznric3741l" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-219" data-mp="3v0d0ys35ga0u">
                  Activate Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-220" data-mp="3s8v7m736z45">
                  Enable or disable Ari skills based on your needs
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-221" data-mp="3cbt0xnysjnhq">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-222" data-mp="3lb6d4oeauq65">
                  <PlugIcon size={24} className="text-white" data-id="element-223" data-mp="34jnh0vn9gy5" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-224" data-mp="3v8u4r5glzhk3">
                  Manage Integrations
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-225" data-mp="cv4f24cwhl7k">
                  Add or remove integrations with third-party services
                </p>
              </div>
            </div>

            <div className="mt-12 text-center" data-id="element-226" data-mp="muvac89ac48p">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-227" data-mp="huz5k6srk8s3">
                Start Chatting
              </a>
            </div>
          </div>
        </section>

        {/* Cloud Section */}
        <section id="cloud" className="min-h-screen flex items-center py-32 px-6" data-id="element-228" data-mp="13p67bvm7k3bd">
          <div className="max-w-7xl mx-auto" data-id="element-229" data-mp="1cnrpvqrojic5">
            <div className="text-center mb-16" data-id="element-230" data-mp="1cmglppubo8ki">
              <div className="inline-block mb-6" data-id="element-231" data-mp="1hmuvpdfhhsl6">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-232" data-mp="1ho5zvecud2ct">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#E9A165] bg-clip-text text-transparent" data-id="element-233" data-mp="3je7nd0sfy3za">
                    CLOUD
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-234" data-mp="3gub7uc984cmb">
                Your Personal Hub
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-235" data-mp="3c1i67x91xkeg">
                AI-powered cloud storage that remembers everything and keeps you
                in control
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-236" data-mp="326q72oztmphk">
              <div data-id="element-237" data-mp="3mrglqc1mbdb">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-238" data-mp="3l6u8nzvtyqj">
                  Intelligent Storage
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-239" data-mp="3pu7uk8c1z1eq">
                  Your personal AI hub that stores files, remembers context, and
                  keeps everything in sync across all your devices.
                </p>

                {/* AI Memory */}
                <div className="mb-8" data-id="element-240" data-mp="2ecycnq8kohfj">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-241" data-mp="29fyee1hysiyz">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-242" data-mp="2z4dci1itj48f">
                      <BrainIcon size={20} className="text-white" data-id="element-243" data-mp="344hr1m061ow0" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-244" data-mp="2q4ql88jszet3">AI Memory</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-245" data-mp="2u6brhb1u0uub">
                    <div className="flex items-start space-x-3" data-id="element-246" data-mp="2u73mokrioqk2">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-247" data-mp="3hvej9bq6k42j">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-248" data-mp="3mvsl2c16uhs4" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-249" data-mp="3d44vyhs3tctt">
                        Remembers your preferences and habits
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-250" data-mp="4m8for6tituu">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-251" data-mp="9g360dgxi72w">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-252" data-mp="1u3dr0cc67ktb" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-253" data-mp="1p4atre2fmivw">
                        Recalls previous conversations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-254" data-mp="1yxhx6tdm3otc">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-255" data-mp="1b62rqrqjgrdv">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-256" data-mp="168t51jujattq" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-257" data-mp="1l6d7ok3ykcwq">
                        View and edit what AI stores about you
                      </p>
                    </div>
                  </div>
                </div>

                {/* Files & Knowledge */}
                <div className="mb-8" data-id="element-258" data-mp="2rldv21ex3mrp">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-259" data-mp="2rkcexij1oa34">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-260" data-mp="m7vkpbp45tjt">
                      <FileIcon size={20} className="text-white" data-id="element-261" data-mp="h9bzjdicglsi" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-262" data-mp="v07acvcelcyq">
                      Files & Knowledge
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-263" data-mp="39ay9wy63cci">
                    <div className="flex items-start space-x-3" data-id="element-264" data-mp="3a2r500dqvz3">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-265" data-mp="3uh5k9wzs3cby">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-266" data-mp="cbshs3mftqvt" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-267" data-mp="7dipq1w0f6ef">
                        Upload documents and notes
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-268" data-mp="1ipruhhkamg9l">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-269" data-mp="1r7tseond2cdi">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-270" data-mp="1v9ykvk22p2yk" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-271" data-mp="1hape1azrbl79">
                        Ask AI questions about your files
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-272" data-mp="29xjpxjiidc94">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-273" data-mp="2a0fmb3epjsvg">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-274" data-mp="208rwt81polwm" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-275" data-mp="256l3s3au9jxy">
                        Import from Google Drive, OneDrive, Dropbox
                      </p>
                    </div>
                  </div>
                </div>

                {/* Devices */}
                <div data-id="element-276" data-mp="2xpgzt52edu7l">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-277" data-mp="37je4y6ypxmsp">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-278" data-mp="37hjs7fxlp0zp">
                      <SmartphoneIcon size={20} className="text-white" data-id="element-279" data-mp="2xkx8iuh0gfz9" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-280" data-mp="2tkehv3qutfyn">
                      Device Management
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-281" data-mp="2omv2u1mc0z5n">
                    <div className="flex items-start space-x-3" data-id="element-282" data-mp="2jts4zhaiv3jn">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-283" data-mp="2euysns6z1il2">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-284" data-mp="29wnkrn08li5h" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-285" data-mp="25t936rwngtpx">
                        See all connected devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-286" data-mp="gd5du69uq9ny">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-287" data-mp="gcvnghkwa0hq">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-288" data-mp="pdtr2yrf85zs" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-289" data-mp="uco8bw9qpmhs">
                        Control sync frequency
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-290" data-mp="3ttwcixp70f6i">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-291" data-mp="1pal4pvug5av">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-292" data-mp="6m0yv6k6eot4" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-293" data-mp="bg57rvd056wu">
                        Remotely disconnect devices
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-294" data-mp="1lkg4y1joyoiw">
                {/* Media */}
                <div className="mb-8" data-id="element-295" data-mp="1lky71et1bwyq">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-296" data-mp="1glbuz8oy0mri">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-297" data-mp="1gn6h2qbyakx6">
                      <VideoIcon size={20} className="text-white" data-id="element-298" data-mp="1pma0xwixz4cq" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-299" data-mp="24axr1na0qlva">Media</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-300" data-mp="1zbtjf12qsw72">
                    <div className="flex items-start space-x-3" data-id="element-301" data-mp="1zbbezh5xfbo2">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-302" data-mp="2dc4b26bvmeta">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-303" data-mp="29airk24u72a4" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-304" data-mp="iy3bfj8i5ns5">
                        Auto-saves photos/videos from devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-305" data-mp="4t444wx1l2mo">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-306" data-mp="8tmwcs2ukape">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-307" data-mp="3r6ktysc62rge" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-308" data-mp="1l3ipw5bkq">
                        View media timeline anytime
                      </p>
                    </div>
                  </div>
                </div>

                {/* Skills & Integrations */}
                <div className="mb-8" data-id="element-309" data-mp="388rrfr5ggjjj">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-310" data-mp="389td0pk3kofi">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-311" data-mp="3d7l2lcx9rygj">
                      <SettingsIcon size={20} className="text-white" data-id="element-312" data-mp="16iq0tvuk71su" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-313" data-mp="1bf6qwvlbjq8k">
                      Skills & Integrations
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-314" data-mp="150hmplnbvwj6">
                    <div className="flex items-start space-x-3" data-id="element-315" data-mp="14yogg7gmnebp">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-316" data-mp="100d5ftelixrw">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-317" data-mp="v3mq58am867j" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-318" data-mp="1sp0tfwqqc195">
                        Add or remove Ari skills
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-319" data-mp="1iz8wivv5qw1i">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-320" data-mp="1e0xomqofavlx">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-321" data-mp="2bgl7zuu1293k" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-322" data-mp="26ok0snbpv8ke">
                        Manage third-party integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-323" data-mp="2izkrs1lei2ng">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-324" data-mp="2j02v1i6hdh4v">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-325" data-mp="2sxqu2kb4dasu" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-326" data-mp="2x05c526ys6j2">
                        Customize skill settings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-327" data-mp="36ovnh4nw96uq">
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#8846DE] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-328" data-mp="36q70lv38bqyi"></div>
                  <div className="relative bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 overflow-hidden" data-id="element-329" data-mp="3gnl1ungl0968">
                    <img src="https://cdn.magicpatterns.com/uploads/nPnXM4vnWRjVHqvZQRMm5A/image.png" alt="Ari Cloud Dashboard - Account, Memory, Chat History, Media Collection" className="w-full h-auto max-h-[600px] object-cover object-top" data-id="element-330" data-mp="3kpg3jt2fcokj" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-331" data-mp="err0972xtnrn">
              <a href="https://cloud.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#8846DE] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-332" data-mp="3n8vmvse6sj2z">
                Explore User Cloud
              </a>
            </div>
          </div>
        </section>

        {/* Business Section */}
        <section id="business" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-333" data-mp="2zkchbkmiukel">
          <div className="max-w-7xl mx-auto" data-id="element-334" data-mp="1f0pfbqz3maon">
            <div className="text-center mb-16" data-id="element-335" data-mp="1jta76myg2dpj">
              <div className="inline-block mb-6" data-id="element-336" data-mp="1ju1rh1mlcp0i">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-337" data-mp="161vjw3xi7qco">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-338" data-mp="1a3723oe1t9au">
                    BUSINESS
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-339" data-mp="1ortryk1axjgb">
                Enterprise Control
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-340" data-mp="1tq4zup81djvw">
                Fleet management and analytics for organizations
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-341" data-mp="ibhrgcb0bphk">
              <div className="relative" data-id="element-342" data-mp="i9esv3vsgo18">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-343" data-mp="3ckotdy4ulg06"></div>
                <div className="relative aspect-video bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl border-2 border-white/10 flex items-center justify-center overflow-hidden" data-id="element-344" data-mp="3hh5iovnsa0eu">
                  <img src="https://cdn.magicpatterns.com/uploads/iRVkz4jdtoc25hx5UZzgBd/business_cloud.png" alt="Ari Business Cloud - Enterprise Dashboard with Device Status, Firmware, and Reporting" className="w-full h-full object-cover" data-id="element-345" data-mp="3qivjut7gwdpt" />
                </div>
              </div>
              <div data-id="element-346" data-mp="37l2ezlda9tps">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-347" data-mp="37llxb36uik4c">
                  Enterprise-Grade Management
                </h3>
                <p className="text-gray-300 mb-10 text-lg leading-relaxed" data-id="element-348" data-mp="2b13ip5g5tw01">
                  Manage fleets of AR devices, deploy updates, monitor usage,
                  and maintain security policies at scale.
                </p>
                <div className="space-y-6 mb-10" data-id="element-349" data-mp="22ldx9w02zi1s">
                  <div className="flex items-start space-x-4 group" data-id="element-350" data-mp="22jt5ge55id7m">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-351" data-mp="1yizhxlvmf0f9">
                      <UsersIcon size={24} className="text-white" data-id="element-352" data-mp="1omwduah7krpo" />
                    </div>
                    <div data-id="element-353" data-mp="1jumjmi9295hl">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-354" data-mp="1jtbfghbpdvpy">
                        Fleet Management
                      </h4>
                      <p className="text-gray-400" data-id="element-355" data-mp="1av9j4f2wzr90">
                        Centralized control of all devices
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-356" data-mp="10wk36rukwgve">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-357" data-mp="10xbtbn5epmea">
                      <LockIcon size={24} className="text-white" data-id="element-358" data-mp="w5y3vqbmbs9b" />
                    </div>
                    <div data-id="element-359" data-mp="hcl78qeisrro">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-360" data-mp="m9v4gs85i7q2">
                        Security Policies
                      </h4>
                      <p className="text-gray-400" data-id="element-361" data-mp="1a0qq17m0k6ov">
                        Enterprise-grade security controls
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-362" data-mp="10ae7fkxrenld">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-363" data-mp="158pgvuk56w32">
                      <TrendingUpIcon size={24} className="text-white" data-id="element-364" data-mp="1xoqgs7jbrx3g" />
                    </div>
                    <div data-id="element-365" data-mp="3if5lkc4imf5n">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-366" data-mp="3iedosxz6mbds">
                        Analytics & Insights
                      </h4>
                      <p className="text-gray-400" data-id="element-367" data-mp="3de84y3z5lt69">
                        Usage metrics and performance data
                      </p>
                    </div>
                  </div>
                </div>
                <a href="https://business.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-368" data-mp="3n8hyvq3quzj3">
                  Enterprise Solutions
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section id="mobile-app" className="min-h-screen flex items-center py-32 px-6" data-id="element-369" data-mp="jpye18neavyb">
          <div className="max-w-7xl mx-auto" data-id="element-370" data-mp="1f0n59awvl0q2">
            <div className="text-center mb-16" data-id="element-371" data-mp="1f1y7v7el42fl">
              <div className="inline-block mb-6" data-id="element-372" data-mp="1j28cogqn4msv">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-373" data-mp="1j308nso5gmjo">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-374" data-mp="1t0ecmssvziv3">
                    MOBILE APP
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-375" data-mp="21y88y3qnici9">
                Ari Mobile
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-376" data-mp="26t43bajd1byj">
                Your AR device companion for iOS and Android - setup, control,
                and manage everything
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-377" data-mp="2lnimbb2xcld7">
              <div data-id="element-378" data-mp="a0f4u2m77wmz">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-379" data-mp="a0yn5kfrgn1j">
                  Easy Setup & Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-380" data-mp="500v8g4y8emu">
                  Get started in seconds with automatic device discovery and
                  one-tap pairing. Manage all your AR devices from one place.
                </p>

                {/* Easy Setup */}
                <div className="mb-8" data-id="element-381" data-mp="spv64s5pjt4g">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-382" data-mp="st0weaq2p11r">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-383" data-mp="nslldpt9k5my">
                      <ZapIcon size={20} className="text-white" data-id="element-384" data-mp="e2a4z3se83z6" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-385" data-mp="e0fsmdv6tk6p">Easy Setup</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-386" data-mp="1bjiivxkbb0eo">
                    <div className="flex items-start space-x-3" data-id="element-387" data-mp="16q5yy0qkvhs9">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-388" data-mp="30z1kzsys4yio">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-389" data-mp="35wl2d1qrxreu" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-390" data-mp="2r8gt0odm2s8q">
                        Automatically finds AR devices nearby
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-391" data-mp="2i8247f1fuv0h">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-392" data-mp="2m9x54ifgj6dq">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-393" data-mp="28axifgbi91k7" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-394" data-mp="2daa3bvitfyk5">
                        One-tap pairing with your device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-395" data-mp="bhlw7b31wqz3">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-396" data-mp="bh2m2gjn76vm">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-397" data-mp="1r11xc3yekwhq" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-398" data-mp="25pg1ci7k2qzv">
                        Connect to Bluetooth headphones
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-399" data-mp="20wwltfpb5lsf">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-400" data-mp="2ep2z8u12teo6">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-401" data-mp="29tdxck1l3lop" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-402" data-mp="2om7oj2q7xq97">
                        Quickly send Wi-Fi login details
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Controls */}
                <div className="mb-8" data-id="element-403" data-mp="pchm83fj327j">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-404" data-mp="pdssc9wg4m1t">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-405" data-mp="3hr6w26r5p9p">
                      <SettingsIcon size={20} className="text-white" data-id="element-406" data-mp="d83opmn6n4c5" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-407" data-mp="dag6nzsxxep7">
                      Device Controls
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-408" data-mp="3gq407bry5rui">
                    <div className="flex items-start space-x-3" data-id="element-409" data-mp="3lnvsi6wi7fz8">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-410" data-mp="3lo5jnxtabt6i">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-411" data-mp="3uoll71qgwq8q" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-412" data-mp="15x15flbqwt2s">
                        Manage multiple headsets from one phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-413" data-mp="1a0fjw7k1d1e4">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-414" data-mp="2uf3qx8w1jt1e">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-415" data-mp="2qgwh6xzzvrzp" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-416" data-mp="2llqongc37f2n">
                        Adjust volume, brightness, and settings
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-417" data-mp="2br73k3aptvij">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-418" data-mp="26pri4a4mlmzm">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-419" data-mp="22pheh44b38nx" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-420" data-mp="1xs7foxv11knf">
                        Manage profile and subscription status
                      </p>
                    </div>
                  </div>
                </div>

                {/* Media & Chat */}
                <div data-id="element-421" data-mp="p9m2wdy61w5q">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-422" data-mp="p8t2dupsw2w1">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-423" data-mp="k9zldsbb1tr4">
                      <MessageSquareIcon size={20} className="text-white" data-id="element-424" data-mp="fhoo6x7y8m29" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-425" data-mp="ak6ciz2jrsqk">
                      Media & Chat
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-426" data-mp="1j8gb2ycopij">
                    <div className="flex items-start space-x-3" data-id="element-427" data-mp="1ignfzw515vy">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-428" data-mp="3sr46282zuezt">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-429" data-mp="3nsszq7bwqmmc" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-430" data-mp="1qwlnczxukrnc">
                        View and delete photos/videos from device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-431" data-mp="1til0ouoglgfl">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-432" data-mp="1yhfj3viil2z6">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-433" data-mp="239fnq19j4k23" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-434" data-mp="2878t4s30da1b">
                        Talk to Ari on your phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-435" data-mp="1fijqupq7uynz">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-436" data-mp="1kifswjfcsg0w">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-437" data-mp="1phsqnz7rj7i9" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-438" data-mp="2vwjn9qpus66h">
                        View Ari conversation history
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-439" data-mp="10ugadyryiid0">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-440" data-mp="10slniex4kg6a">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-441" data-mp="19v4p6vp6cmqy" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-442" data-mp="15tsvadz9pfcy">
                        Add and remove Ari integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-443" data-mp="i1uh9m8fp5w4">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-444" data-mp="d65c93dnawsf">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-445" data-mp="qyd88sx3ujq2" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-446" data-mp="m3pg8k2w776q">
                        Customize profile and themes
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative flex items-center justify-center" data-id="element-447" data-mp="3gfa6roepcnci">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8846DE] to-[#D25796] rounded-[3rem] opacity-20 blur-3xl" data-id="element-448" data-mp="3l6s5s6x60duu"></div>

                {/* Phone Mockup */}
                <div className="relative w-[340px] h-[700px]" data-id="element-449" data-mp="3bgql7p40tdko">
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-gradient-to-b from-zinc-700 to-zinc-800 rounded-[3rem] shadow-2xl border-[3px] border-zinc-600/50" data-id="element-450" data-mp="316twvmwu589">
                    {/* Inner bezel */}
                    <div className="absolute inset-[10px] bg-zinc-900 rounded-[2.4rem]" data-id="element-451" data-mp="3p85b8fwcif18">
                      {/* Screen */}
                      <div className="absolute inset-[3px] rounded-[2.2rem] overflow-hidden" data-id="element-452" data-mp="3u6f9izdd9vr2">
                        <img src="https://cdn.magicpatterns.com/uploads/x2mGyh8RJbMReKTSfHvhJC/Screen_Andorid.jpg" alt="Ari Companion App - Device management screen showing Arc3-3813 connected with battery, display, volume controls and quick actions" className="w-full h-full object-cover" data-id="element-453" data-mp="2dsp3vo6lmzfb" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-454" data-mp="2kbuy5dbzu4u6">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-455" data-mp="3hy20egktrl3d">
                <a href="https://play.google.com/store/apps/details?id=com.ari_os.companion&hl=en" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-456" data-mp="3hvpjm6qszgrw">
                  <img src="https://cdn.magicpatterns.com/uploads/grMQzHgPVptgUWdveugAA9/163-1632345_download-the-domus-app-today-android-icon-white.png" alt="Android" className="w-6 h-6" data-id="element-457" data-mp="1rlcuob7cy1g8" />
                  <span data-id="element-458" data-mp="h1rn5tn60zmc">Download for Android</span>
                </a>
                <a href="https://apps.apple.com/ch/app/ari-companion/id6747385775" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-459" data-mp="h0gjdttpzrv8">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" data-id="element-460" data-mp="14rc6idn8dyw5">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" data-id="element-461" data-mp="31l7055nplfh1" />
                  </svg>
                  <span data-id="element-462" data-mp="33fj5ht1kvro9">Download for iOS</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Overview Section */}
        <section id="architecture" className="py-32 px-6" data-id="element-463" data-mp="3de8q3tkuzq87">
          <div className="max-w-7xl mx-auto" data-id="element-464" data-mp="3r6otivho9q9n">
            <div className="text-center mb-16" data-id="element-465" data-mp="3r5dhk8e2pc7g">
              <div className="inline-block mb-6" data-id="element-466" data-mp="3m7lnp8tvbg0m">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-467" data-mp="3m8wrv9r86ps9">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-468" data-mp="x7o8axgzsn">
                    ARCHITECTURE
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-469" data-mp="nmzh13x7u3ka">
                Ecosystem Overview
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-470" data-mp="en40skoszide">
                A unified platform connecting devices, apps, and cloud services
              </p>
            </div>

            {/* Architecture Grid */}
            <div className="relative max-w-6xl mx-auto" data-id="element-471" data-mp="4pg1rik5zopf">
              {/* Connection Lines Layer - Hidden on mobile */}
              <div className="hidden lg:block absolute inset-0 pointer-events-none" data-id="element-472" data-mp="3n476ez80eroy">
                {/* Companion App to Supported Devices connection (Violet) - Centered vertically */}
                <div className="absolute top-[140px] left-[50%] w-[33.33%] h-1 bg-[#8846DE] shadow-[0_0_15px_rgba(136,70,222,0.5)]" data-id="element-473" data-mp="3d4gdv330emri"></div>

                {/* Ari User Cloud to Business Cloud connection (Red) - Horizontal line between bottom bubbles */}
                <div className="absolute top-[560px] left-[25%] w-[50%] h-1 bg-[#f64f59] shadow-[0_0_15px_rgba(246,79,89,0.5)]" data-id="element-474" data-mp="1e3r5z8eosiw"></div>

                {/* Orange connecting lines - Web Portal to Supported Devices to both Clouds */}
                {/* Horizontal line connecting Web Portal to Supported Devices (bottom of top row cards) - shortened */}
                <div className="absolute top-[360px] left-[12.5%] w-[75%] h-1 bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-475" data-mp="3eo00qzf9b65h"></div>

                {/* Vertical line from horizontal orange line down to Ari User Cloud */}
                <div className="absolute top-[360px] left-[22.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-476" data-mp="35uwhgahxjz2x"></div>

                {/* Vertical line from horizontal orange line down to Business Cloud - centered with Business Cloud bubble */}
                <div className="absolute top-[360px] left-[77.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-477" data-mp="2qyf81otawuj0"></div>

                {/* Vertical line coming down from Web Portal - centered with the card */}
                <div className="absolute top-[280px] left-[12.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-478" data-mp="2lqpazqj0gn4b"></div>

                {/* Vertical line coming down from Supported Devices - centered with the card */}
                <div className="absolute top-[280px] left-[87.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-479" data-mp="1y37kdybhfv2x"></div>
              </div>

              {/* Top Row - 3 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-32 mb-32 relative z-10" data-id="element-480" data-mp="281ez0aiau11r">
                {/* Web Portal */}
                <button onClick={() => scrollToSection('chat')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(253,160,133,0.4),0_0_0_1px_rgba(253,160,133,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#fda085]/40 hover:shadow-[0_30px_80px_-20px_rgba(253,160,133,0.6),0_0_40px_rgba(253,160,133,0.3)] relative overflow-hidden cursor-pointer" data-id="element-481" data-mp="2csnard73pyi3">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(253,160,133,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-482" data-mp="impn90urxzt0"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#fda085]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-483" data-mp="9tcm5bmk3rvu"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-484" data-mp="3w3ox9fqlb2kn">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#fda085]/20 to-[#fda085]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(253,160,133,0.1)] border border-[#fda085]/20 group-hover:shadow-[0_0_30px_rgba(253,160,133,0.4)] transition-all duration-400" data-id="element-485" data-mp="4wcfayhvul3o">
                      <MonitorIcon size={28} className="text-[#fda085] group-hover:scale-110 transition-transform duration-400" data-id="element-486" data-mp="38f0gckhevjas" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-487" data-mp="3hd2fsvlhy3vy">
                      Ari Web
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-488" data-mp="3rcjhl1xmnxmn">
                      Desktop Interface
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-489" data-mp="40926vcamzzx">
                      <span data-id="element-490" data-mp="3w3jg2bmeanr9">• Full chat interface</span>
                      <span data-id="element-491" data-mp="rtzu5hfor2">• File management</span>
                      <span data-id="element-492" data-mp="dxn8w38f0af1">• Settings control</span>
                      <span data-id="element-493" data-mp="dw3pi3rgpj91">• Cross-device sync</span>
                    </div>
                  </div>
                </button>

                {/* Companion App */}
                <button onClick={() => scrollToSection('mobile-app')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(196,113,237,0.4),0_0_0_1px_rgba(196,113,237,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#c471ed]/40 hover:shadow-[0_30px_80px_-20px_rgba(196,113,237,0.6),0_0_40px_rgba(196,113,237,0.3)] relative overflow-hidden cursor-pointer" data-id="element-494" data-mp="32smldqn2gnw0">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(196,113,237,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-495" data-mp="3qj0j9xsc1gyi"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#c471ed]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-496" data-mp="3a3trzyh702c"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-497" data-mp="253taekflym30">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#c471ed]/20 to-[#c471ed]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(196,113,237,0.1)] border border-[#c471ed]/20 group-hover:shadow-[0_0_30px_rgba(196,113,237,0.4)] transition-all duration-400" data-id="element-498" data-mp="1xlmrwz4fcptv">
                      <SmartphoneIcon size={28} className="text-[#c471ed] group-hover:scale-110 transition-transform duration-400" data-id="element-499" data-mp="1iwz1f79fr6as" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-500" data-mp="26kqdc9r2dhbj">
                      Ari Mobile
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-501" data-mp="22i3is130dian">
                      iOS & Android Controller
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-502" data-mp="r3gi6ac80s6v">
                      <span data-id="element-503" data-mp="2ezmm8tosgvde">• Device control</span>
                      <span data-id="element-504" data-mp="2jre5sk9j20u1">• Chat with AI</span>
                      <span data-id="element-505" data-mp="2js62jyev24lw">• Media management</span>
                    </div>
                  </div>
                </button>

                {/* Supported Devices */}
                <button onClick={() => scrollToSection('ari-os')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(233,161,101,0.4),0_0_0_1px_rgba(233,161,101,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#E9A165]/40 hover:shadow-[0_30px_80px_-20px_rgba(233,161,101,0.6),0_0_40px_rgba(233,161,101,0.3)] relative overflow-hidden cursor-pointer" data-id="element-506" data-mp="2a1bcsk2eomvp">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(233,161,101,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-507" data-mp="1da96bxqimlnp"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#E9A165]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-508" data-mp="19sd2sel3qq5k"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-509" data-mp="1jru82uwfz210">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#E9A165]/20 to-[#E9A165]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(233,161,101,0.1)] border border-[#E9A165]/20 group-hover:shadow-[0_0_30px_rgba(233,161,101,0.4)] transition-all duration-400" data-id="element-510" data-mp="1ery3atfx76ke">
                      <LayersIcon size={28} className="text-[#E9A165] group-hover:scale-110 transition-transform duration-400" data-id="element-511" data-mp="10t81x1m2ikq8" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-512" data-mp="8b46jp2yqydq">
                      Supported Devices
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-513" data-mp="t1g5r0q344di">
                      Smart Device Ecosystem
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center mt-2" data-id="element-514" data-mp="x0yr1xltihgv">
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-515" data-mp="11ubfa6ml4m93">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-516" data-mp="a4otvccvgrjh" />
                        RealWear
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-517" data-mp="o23l8mmor9e3">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-518" data-mp="1zgqvz676tfvl" />
                        Rokid
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-519" data-mp="3f1sxr0fcz5x3">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-520" data-mp="358lqtb4yzhuw" />
                        Vuzix
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-521" data-mp="2rax8a9ydkmt0">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-522" data-mp="pjshth86hsa9" />
                        Others
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Bottom Row - 2 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-32 relative z-10" data-id="element-523" data-mp="3ulotlhtesc3x">
                {/* Ari User Cloud */}
                <button onClick={() => scrollToSection('cloud')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-524" data-mp="3kt97c8b2x1d8">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-525" data-mp="w1ovv43e3qd3"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-526" data-mp="2kkt2xq2buv7o"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-527" data-mp="2ubxcga98hnv4">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-528" data-mp="2pkxhvbnl7hvs">
                      <CloudIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-529" data-mp="3da01uq5p1mtg" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-530" data-mp="1ixa580nxxzid">
                      Ari Cloud
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-531" data-mp="1dz8ohmdzmca2">
                      Personal Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-532" data-mp="1qso6u296b0at">
                      <span data-id="element-533" data-mp="25hkdbuvl059b">• AI memory & context</span>
                      <span data-id="element-534" data-mp="20p9b2lddc7dl">• Files & documents</span>
                      <span data-id="element-535" data-mp="20ng7jey1y39t">• Photos & videos</span>
                      <span data-id="element-536" data-mp="2efwcilaikbdd">• Device settings</span>
                      <span data-id="element-537" data-mp="2eisc0e20f83x">• Chat history</span>
                    </div>
                  </div>
                </button>

                {/* Business Cloud */}
                <button onClick={() => scrollToSection('business')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-538" data-mp="2odsnnd3gb6bh">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-539" data-mp="35no3ob1p4l73"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-540" data-mp="2vwthf6og9lln"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-541" data-mp="2mzj4zhzwbqf5">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-542" data-mp="281z4oody2jfb">
                      <BuildingIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-543" data-mp="b745b1f4sdwn" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-544" data-mp="4u8857vo16fs">
                      Ari Business
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-545" data-mp="3rxd3pyle0k1p">
                      Enterprise Control Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex flex-col items-center justify-center gap-1" data-id="element-546" data-mp="nimydsy5sgfj">
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-547" data-mp="irerp4o7r962">
                        <span data-id="element-548" data-mp="equslvk2xvha">• Fleet management</span>
                        <span data-id="element-549" data-mp="eq2y6o27y3sl">• Mass deployment</span>
                      </div>
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-550" data-mp="1b90qfjgqufa4">
                        <span data-id="element-551" data-mp="16gfzqqt4wiat">• Analytics</span>
                        <span data-id="element-552" data-mp="16fo8ttahf8qv">• Security policies</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Developers Section */}
        <section id="developers" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-553" data-mp="2y45m6wuoym8j">
          <div className="max-w-7xl mx-auto" data-id="element-554" data-mp="383d4zzk73931">
            <div className="text-center mb-16" data-id="element-555" data-mp="382lf95da45ko">
              <div className="inline-block mb-6" data-id="element-556" data-mp="3d1y41vnpnmpw">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-557" data-mp="3d0mwrlv23wu1">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-558" data-mp="3lurn846a3691">
                    DEVELOPERS
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-559" data-mp="3vql6s7if000b">
                Build on Ari OS
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-560" data-mp="3d75hu0zgj2px">
                Comprehensive APIs, SDKs, and tools to extend the AriOS
                ecosystem
              </p>
            </div>

            {/* Developer Tools Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-561" data-mp="3my0435cpe2bd">
              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-562" data-mp="2z85s0q07khs6">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-563" data-mp="345xuuf2e5ob5">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-564" data-mp="ogw52i6cw9a1">
                    <CodeIcon size={28} className="text-white" data-id="element-565" data-mp="qxwnfkljvfzm" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-566" data-mp="vzdjn2t3ae9v">
                    REST APIs
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-567" data-mp="i0mcy1gk3uvr">
                    Comprehensive REST APIs for device control, cloud storage,
                    and AI features
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-568" data-mp="19s250b0vu9td">
                    <span data-id="element-569" data-mp="1er6e71nt47jp">API Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-570" data-mp="10y6z30rxsfco" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-571" data-mp="1so1r5576ij51">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-572" data-mp="23b0094ftgi7f">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-573" data-mp="1tbr72dsrz7m4">
                    <PackageIcon size={28} className="text-white" data-id="element-574" data-mp="1odz8x21jep9h" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-575" data-mp="1jkeko1pqx9nh">SDKs</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-576" data-mp="1flfekdsa36ug">
                    Native SDKs for iOS, Android, Web, and AR device development
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-577" data-mp="15lwvdzk6zp3g">
                    <span data-id="element-578" data-mp="10ndbe4p5sndq">Download SDKs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-579" data-mp="10nn2jvlxx0l0" />
                  </div>
                </div>
              </a>

              <a href="https://github.com/realwear" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-580" data-mp="vk2s5k8gndoq">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-581" data-mp="7tq9bebofwjd">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-582" data-mp="hr4qxh9c6rd9">
                    <GitBranchIcon size={28} className="text-white" data-id="element-583" data-mp="miwa36q5xutd" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-584" data-mp="3l7bhqn6nku4m">GitHub</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-585" data-mp="3q5dbq35maduv">
                    Open-source examples, sample apps, and community
                    contributions
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-586" data-mp="1476tochhzb8s">
                    <span data-id="element-587" data-mp="1i6y4k4cpwbik">View Repos</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-588" data-mp="1i3tu4p8nsjjg" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-589" data-mp="1n5j7ibjct9ap">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-590" data-mp="2aulp5jdzn257">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-591" data-mp="25xc0w72c4wiy">
                    <BookOpenIcon size={28} className="text-white" data-id="element-592" data-mp="2thusf7nbjh6p" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-593" data-mp="2fjwg07sk5utu">
                    Documentation
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-594" data-mp="2kgmuiqopsid5">
                    Comprehensive guides, tutorials, and best practices
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-595" data-mp="2akjvhtp5szuf">
                    <span data-id="element-596" data-mp="1wsvfhfbshr86">Read Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-597" data-mp="1wu6lllspjb2g" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-598" data-mp="1mws89v22z2ed">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-599" data-mp="1sqafgrrjp0qu">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-600" data-mp="21pxl5prb4spp">
                    <TerminalIcon size={28} className="text-white" data-id="element-601" data-mp="2bmshc203yh0t" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-602" data-mp="2gdsen8d5313u">
                    CLI Tools
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-603" data-mp="2ld4xli0w3o15">
                    Command-line tools for development, testing, and deployment
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-604" data-mp="2udjkumxezd7a">
                    <span data-id="element-605" data-mp="2ub78cpafdv1m">Install CLI</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-606" data-mp="ir9bairdvk0s" />
                  </div>
                </div>
              </a>

              <a href="https://discord.com/invite/mkG5eVXrkS" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-607" data-mp="9prz3bh3k2hd">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-608" data-mp="xg5hsbdskoza">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-609" data-mp="npatl1gzjfb7">
                    <UsersIcon size={28} className="text-white" data-id="element-610" data-mp="1l5zsonejw3gw" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-611" data-mp="1gcdrbrdu7m0e">
                    Community
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-612" data-mp="1zca5bdz3u4fl">
                    Join our developer community, forums, and Discord server
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-613" data-mp="1qh3ycbo3lgbh">
                    <span data-id="element-614" data-mp="1uifg5v0qcx94">Join Community</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-615" data-mp="1uh4b7rvjtjgf" />
                  </div>
                </div>
              </a>
            </div>

            {/* Code Example */}
            <div className="relative" data-id="element-616" data-mp="17l422oo37qm3"></div>

            {/* OEM CTA */}
            <div className="mt-20 text-center" data-id="element-617" data-mp="1ble8g2fskj1h">
              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-618" data-mp="31x2aioekoigx">
                AR/AI Device Manufacturer?
              </h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg" data-id="element-619" data-mp="35yxczw88p1wa">
                Join the Ari OS ecosystem and bring AI-powered voice control to
                your devices
              </p>
              <button onClick={() => setContactModalOpen(true)} className="inline-block px-10 py-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-620" data-mp="qai1m61lx5s2">
                Partner With Us
              </button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6" data-id="element-621" data-mp="18zx1hx5k3ouv">
          <div className="max-w-5xl mx-auto text-center" data-id="element-622" data-mp="1mzpmdbuejaux">
            <div className="relative" data-id="element-623" data-mp="1mz6e6muk00u3">
              <div className="absolute -inset-8 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-3xl" data-id="element-624" data-mp="1i14ohj2mh39n"></div>
              <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-16 border-2 border-white/10" data-id="element-625" data-mp="3orukx66ep6em">
                <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-626" data-mp="3m6wk7kcglc67">
                  Ready to Experience
                  <br data-id="element-627" data-mp="3q7yl05m0xwaa" />
                  the Future?
                </h2>
                <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" data-id="element-628" data-mp="3v7l06klexmlq">
                  Join thousands of users already working hands-free with AriOS
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-629" data-mp="3z75zvjyeclg">
                  <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-12 py-6 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-xl group relative overflow-hidden" data-id="element-630" data-mp="32kqg3osicp8s">
                    <span className="relative z-10" data-id="element-631" data-mp="2jqbio3luh4n2">Get Started Free</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-632" data-mp="2opxvwd1oakvv"></div>
                  </a>
                  <button onClick={() => scrollToSection('architecture')} className="px-12 py-6 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-xl" data-id="element-633" data-mp="2ca7yrqpvq9u9">
                    Ecosystem Architecture
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-16 px-6" data-id="element-634" data-mp="1okdlx95k8la0">
          <div className="max-w-7xl mx-auto" data-id="element-635" data-mp="1jkhibb0ryvuz">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-636" data-mp="1jlsmhby4u5mm">
              <div className="flex items-center space-x-3" data-id="element-637" data-mp="1ejuwlyv88ckp">
                <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-8" data-id="element-638" data-mp="1ajt5gjn4b1jo" />
              </div>
              <div className="flex flex-col items-center md:items-end gap-2" data-id="element-639" data-mp="14ylijq72e7x8">
                <p className="text-gray-400 text-center md:text-right" data-id="element-640" data-mp="qyaram1uerbj">
                  © 2026 AriOS created by RealWear. All rights reserved.
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-400" data-id="element-641" data-mp="vyyjhb1t5e7c">
                  <a href="https://support.realwear.com/hubfs/Ari/ari_terms_of_use_realwear.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-642" data-mp="1jn89kp620o4a">
                    Terms of Use
                  </a>
                  <span data-id="element-643" data-mp="22lkkoggj082i">|</span>
                  <a href="/conditions" className="hover:text-white transition-colors" data-id="element-644" data-mp="22k9ek9zlyo88">
                    Conditions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Contact Modal */}
      {contactModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-645" data-mp="2qgmhuu3t9r23">
          <div className="relative w-full max-w-lg bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border-2 border-white/10 shadow-2xl" data-id="element-646" data-mp="2lh195ryua4c6">
            {/* Close button */}
            <button onClick={() => setContactModalOpen(false)} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-647" data-mp="1tqd9kkctd70m">
              <XIcon size={24} data-id="element-648" data-mp="22qig6sa6o1dq" />
            </button>

            <div className="p-10 text-center" data-id="element-649" data-mp="22t4o4t0zkiwh">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-650" data-mp="3j4070619pibn">
                <MessageSquareIcon size={32} className="text-white" data-id="element-651" data-mp="1chawsdebjzie" />
              </div>

              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-652" data-mp="1gid1vaux35sa">
                Partner With Ari OS
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-653" data-mp="1lhh8puudcrfg">
                Interested in bringing Ari OS to your AR or AI device? We'd love
                to hear from you. Send us an email describing your device and
                use case, and we'll happily set up a meeting to explore the
                partnership.
              </p>

              {/* Email */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6" data-id="element-654" data-mp="248gvqqiazr63">
                <p className="text-zinc-400 text-sm mb-2" data-id="element-655" data-mp="297bj85r7u3wj">
                  Send your inquiry to
                </p>
                <a href="mailto:ingrid.maes@realwear.com" className="text-xl font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent hover:opacity-80 transition-opacity" data-id="element-656" data-mp="9uplbos5h39l">
                  ingrid.maes@realwear.com
                </a>
              </div>

              {/* What to include */}
              <div className="text-left" data-id="element-657" data-mp="3tp2u79cv4jxe">
                <p className="text-zinc-400 text-sm mb-3" data-id="element-658" data-mp="3tpvupsl8ad73">
                  Please include in your email:
                </p>
                <div className="space-y-2" data-id="element-659" data-mp="3ooevs2mb10t5">
                  <div className="flex items-start space-x-3" data-id="element-660" data-mp="q8vufdxkj6tw">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-661" data-mp="l9j8cveiu3sd">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-662" data-mp="g9ohusynrg7u" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-663" data-mp="c9mco9tps4nt">
                      Your company and role
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-664" data-mp="30ykkvqljpmbj">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-665" data-mp="2wyaewdxr6vwo">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-666" data-mp="1wf4g920jrxdi" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-667" data-mp="21e7oevbtqjpp">
                      A brief description of your device
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-668" data-mp="2b755qy52y2mq">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-669" data-mp="2g5ghjeexqn7l">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-670" data-mp="2k5qp2vidllok" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-671" data-mp="2p6wfgmokcwux">
                      How you'd like to integrate with Ari OS
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
            </div>
          </div>
        </div>}

      <style data-id="element-672" data-mp="zrzqyvrocdmr">{`
        /* Apple-inspired smooth animations */
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        
        @keyframes grid-flow {
          0% { transform: translateY(0); }
          100% { transform: translateY(100px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(136, 70, 222, 0.3); }
          50% { box-shadow: 0 0 40px rgba(136, 70, 222, 0.6), 0 0 60px rgba(233, 161, 101, 0.4); }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 15s ease infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        .animate-grid-flow {
          animation: grid-flow 20s linear infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        
        .animate-glow-pulse {
          animation: glow-pulse 3s ease-in-out infinite;
        }
        
        /* Glass morphism effects */
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px 0 rgba(0, 0, 0, 0.37),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.05);
        }
        
        .glass-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 
            0 12px 48px 0 rgba(0, 0, 0, 0.5),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(136, 70, 222, 0.2);
        }
        
        /* Smooth scroll */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 10px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.3);
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #E9A165, #8846DE, #D25796);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #D25796, #8846DE, #E9A165);
        }
      `}</style>
    </div>;
}